// Copyright 2020 TOKU Systems Inc.

import React, { useState } from 'react';
import { EntityRef, Signal } from '@equestria/spike';
import { GithubPicker, ColorResult } from 'react-color';
import { SignalPlot } from './signal-plot';
import {
  Card,
  CardHeader,
  IconButton,
  Popover,
  CardActions,
  Icon,
  makeStyles,
  createStyles,
} from '@material-ui/core';
import { colorPalette, SignalCursorValue } from './signal-plot';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      paddingBottom: '0px',
      '& .MuiCardHeader-content': {
        flexBasis: '90%',
        flexGrow: 0,
        minWidth: 0,
      },
    },
    action: {
      paddingTop: '0px',
    },
  })
);

interface SignalItemProps {
  itemIndex: number;
  signalPlot: SignalPlot;
  originName?: string;
  cursorValue?: SignalCursorValue;
  onColorChange?: (itemIndex: number, color: string) => void;
  onDismiss?: (itemIndex: number) => void;
  onVisibilityChange?: (itemIndex: number, visible: boolean) => void;
  onInfoClick: (signal: Signal) => void;
}

export default function SignalItem(props: SignalItemProps): JSX.Element {
  const [visible, setVisible] = useState(props.signalPlot?.visible ?? true);
  const [color, setColor] = useState(
    props.signalPlot.styles?.line?.color?.toString() ??
      colorPalette.get(0) ??
      '#000000'
  );
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const [colorPickerAnchorElement, setColorPickerAnchorElement] =
    useState<HTMLButtonElement | null>(null);

  const classes = useStyles();

  const getOriginName = (): string => {
    if (props.originName) {
      return props.originName;
    } else if (props.signalPlot.signal.origin) {
      const origin = props.signalPlot.signal.origin;
      if (typeof origin === 'object') {
        const shortName = (origin as EntityRef).shortName;
        if (shortName === undefined) {
          if (origin.slug === undefined) {
            if (origin.id === undefined) {
              return '???';
            } else {
              return origin.entityType + '!' + origin.id;
            }
          } else {
            return origin.entityType + '.' + origin.slug;
          }
        } else {
          return shortName;
        }
      } else {
        return origin;
      }
    } else {
      return '???';
    }
  };

  const handleCloseClick = (): void => {
    if (props.onDismiss) {
      props.onDismiss(props.itemIndex);
    }
  };

  const handleColorClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    setColorPickerAnchorElement(event.currentTarget);
    setColorPickerOpen(true);
  };

  const handleColorChange = (color: ColorResult): void => {
    if (props.onColorChange && props.signalPlot.signal.id) {
      props.onColorChange(props.itemIndex, color.hex);
    }

    setColor(color.hex);
    setColorPickerOpen(false);
    setColorPickerAnchorElement(null);
  };

  const handleCloseColorPicker = (): void => {
    setColorPickerOpen(false);
  };

  const handleVisibilityClick = (): void => {
    if (props.onVisibilityChange) {
      props.onVisibilityChange(props.itemIndex, !visible);
    }

    setVisible(!visible);
  };

  return (
    <Card>
      <CardHeader
        className={classes.root}
        title={props.signalPlot.signal.name ?? '???'}
        titleTypographyProps={{ variant: 'subtitle2', noWrap: true }}
        subheader={getOriginName()}
        subheaderTypographyProps={{ variant: 'caption', noWrap: true }}
        action={
          <IconButton edge="start" size="small" onClick={handleCloseClick}>
            <Icon fontSize="small">close</Icon>
          </IconButton>
        }
      />
      <CardActions className={classes.action}>
        <IconButton edge="start" size="small" onClick={handleColorClick}>
          <Icon fontSize="default" style={{ color }}>
            stop
          </Icon>
        </IconButton>
        <IconButton edge="start" size="small" onClick={handleVisibilityClick}>
          {visible ? (
            <Icon fontSize="small">visibility</Icon>
          ) : (
            <Icon fontSize="small">visibility_off</Icon>
          )}
        </IconButton>

        <IconButton
          edge="end"
          size="small"
          style={{ marginLeft: 'auto' }}
          onClick={(): void => props.onInfoClick(props.signalPlot.signal)}
        >
          <Icon fontSize="small">info</Icon>
        </IconButton>
        <Popover
          open={colorPickerOpen}
          anchorEl={colorPickerAnchorElement}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={handleCloseColorPicker}
        >
          <GithubPicker
            triangle="hide"
            onChangeComplete={handleColorChange}
            colors={colorPalette.toArray()}
          />
        </Popover>
      </CardActions>
    </Card>
  );
}
